import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
// Optional SEO support (enable if your app uses react-helmet-async)
// import { Helmet } from "react-helmet-async";
import Posts from "./Posts.js";
import Pagination from "./Pagination.js";
import { QA_API_URL } from "../../Constants";
import softwareImage from "../../images/software-image.png";
import supportImage from "../../images/24-7-support.png";
import locationImage from "../../images/location-herndon.png";

/* ------------------------------------------------------------------
   AboutUs page — modernized UI, loading/empty/error states, SEO hooks
------------------------------------------------------------------- */
export default function AboutUsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(10);

  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${QA_API_URL}/qa/all`);
        setPosts(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        setError("We couldn't load the latest Q&A. Please try again in a bit.");
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  // get current posts
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);

  // change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="container-fluid p-0" role="main">
      {/* <Helmet>
        <title>About Us — TechVenture LLC</title>
        <meta name="description" content="TechVenture LLC helps developers learn faster, ace interviews, and showcase verified certificates on LinkedIn. Based in the Washington metro area, serving teams globally." />
        <link rel="canonical" href="https://techventurellc.com/about" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "TechVenture LLC",
          url: "https://techventurellc.com/",
          logo: "https://techventurellc.com/logo.png",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Herndon",
            addressRegion: "VA",
            addressCountry: "USA"
          }
        })}</script>
      </Helmet> */}

      {/* Hero */}
      <section className="py-5 text-white text-center" style={{ background: "linear-gradient(135deg, #0d6efd 0%, #6610f2 45%, #6f42c1 100%)" }}>
        <div className="container">
          <span className="badge bg-light text-dark mb-3">About TechVenture LLC</span>
          <h1 className="display-6 fw-bold mb-3">We help developers learn, practice, and get recognized</h1>
          <p className="lead mb-0 opacity-90">Since 2022, we’ve supported individuals and teams with learning paths, interview prep, hands‑on labs, and shareable certificates.</p>
        </div>
      </section>

      {/* Mission + Snapshot */}
      <section className="container py-5">
        <div className="row g-4 align-items-center">
          <div className="col-lg-7">
            <h2 className="h3 mb-3">Our mission</h2>
            <p>
              TechVenture LLC is an IT consulting and learning platform that delivers practical, high‑quality resources to help you build secure systems and advance your career. From the Washington D.C. metro area, we serve learners and organizations across the globe—combining curated content with real‑world engineering experience.
            </p>
            <div className="row text-center mt-4 g-3">
              {[{k:"Founded",v:"2022"},{k:"Learners",v:"25K+"},{k:"Interview Qs",v:"9K+"},{k:"Certificates",v:"30+"}].map(s => (
                <div className="col-6 col-md-3" key={s.k}>
                  <div className="fw-bold fs-4">{s.v}</div>
                  <div className="text-muted small">{s.k}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body">
                <h3 className="h5">What we provide</h3>
                <ul className="mb-0">
                  <li>Learning paths for cutting edge technologies</li>
                  <li>System Design and Coding Interview practice</li>
                  <li>Hands‑on labs with DevOps & cloud tooling</li>
                  <li>Trainings, verified badges & certifications</li>
                  <li>Consulting for small & medium‑size teams</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values cards (Who / What / Where) */}
      <section className="container pb-2">
        <div className="row row-cols-1 row-cols-md-3 g-4">
          <div className="col">
            <div className="card h-100 shadow-sm border-0">
              <img className="card-img-top" src={softwareImage} alt="Developing secure, user‑friendly software" loading="lazy" />
              <div className="card-body">
                <h3 className="h5 card-title">Who we are</h3>
                <p className="card-text">We build secure, user‑friendly systems—prioritizing correctness, performance, and maintainability.</p>
                <p className="card-text"><small className="text-muted">Last updated 3 days ago</small></p>
              </div>
            </div>
          </div>
          <div className="col">
            <div className="card h-100 shadow-sm border-0">
              <img className="card-img-top" src={supportImage} alt="24/7 client support" loading="lazy" />
              <div className="card-body">
                <h3 className="h5 card-title">What we live by</h3>
                <p className="card-text">We support clients whenever they need us—thoughtful communication, clear SLAs, and reliable outcomes.</p>
                <p className="card-text"><small className="text-muted">Last updated 3 days ago</small></p>
              </div>
            </div>
          </div>
          <div className="col">
            <div className="card h-100 shadow-sm border-0">
              <img className="card-img-top" src={locationImage} alt="Herndon VA location" loading="lazy" />
              <div className="card-body">
                <h3 className="h5 card-title">Where we're located</h3>
                <p className="card-text">We’re in the heart of Herndon, VA—close to the capital—and always just a message away.</p>
                <p className="card-text"><small className="text-muted">Last updated 3 days ago</small></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Narrative section (modernized original copy) */}
      <section className="container py-4">
        <div className="row">
          <div className="col-12">
            <div className="alert alert-secondary" role="note">
              Since 2022, TechVenture LLC has provided full‑featured, high‑quality hosting and consulting to small and medium businesses in the Washington metro area. We help you get online fast and keep your systems secure, observable, and scalable—from day one.
            </div>
            <p>
              Our team supports you across the entire software development lifecycle—from discovery and architecture to implementation, testing, and hand‑off. Thank you for trusting us with your growth.
            </p>
          </div>
        </div>
      </section>

      {/* Latest Q&A from API */}
      <section className="container py-5 border-top">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
          <h2 className="h5 text-uppercase text-muted mb-0">Latest Q&A</h2>
          <Link to="/codingRound" className="btn btn-sm btn-outline-primary">Practice Coding</Link>
        </div>

        {loading && (
          <div className="row g-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div className="col-12" key={i}>
                <div className="placeholder-glow">
                  <span className="placeholder col-8" />
                  <span className="placeholder col-6" />
                  <span className="placeholder col-4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-warning" role="alert">{error}</div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="text-muted">No Q&A yet. Check back soon.</div>
        )}

        {!loading && !error && posts.length > 0 && (
          <>
            <Posts posts={currentPosts} />
            <Pagination postsPerPage={postsPerPage} totalPosts={posts.length} paginate={paginate} />
          </>
        )}
      </section>

      {/* Contact CTA */}
      <section className="py-5 bg-dark text-white text-center mt-4">
        <div className="container">
          <h2 className="fw-bold mb-2">Want to collaborate with TechVenture?</h2>
          <p className="text-white-50 mb-4">We partner with teams for training, content, and platform engineering.</p>
          <Link to="/contactUs" className="btn btn-primary btn-lg">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}
